import { useEffect, useState, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle
} from "@/components/dialog";
import { cn } from "@/utils";
import SortableRow from "./SortableRow";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/button";
import { GridSettingsSchema } from "./schema";
import type { GridSettingsValues } from "./schema";
import type { GridPayload, Column } from "./types";

export type GridSettingsModalProps = {
  isOpen: boolean;
  headerClassname?: string;
  descriptionClassname?: string;
  addButtonClassname?: string;
  saveButtonClassname?: string;
  availableColumns: Column[];
  currentColumns: Column[];
  limit?: number;
  onClose: () => void;
  onSaveColumns?: (payload: GridPayload, currentColumns: Column[]) => void;
};

const GridSettingsModal = ({
  isOpen = false,
  headerClassname,
  descriptionClassname,
  addButtonClassname,
  saveButtonClassname,
  availableColumns,
  currentColumns,
  limit = availableColumns.length,
  onClose,
  onSaveColumns
}: GridSettingsModalProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const form = useForm<GridSettingsValues>({
    resolver: zodResolver(GridSettingsSchema),
    defaultValues: { columns: currentColumns },
    mode: "onChange"
  });
  const { control, trigger } = form;
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "columns",
    keyName: "fieldId"
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ columns: currentColumns });
    }
  }, [isOpen, currentColumns, form]);

  const addColumn = async () => {
    const isValid = await trigger("columns");

    if (isValid) {
      append({ id: "" });

      // Auto scroll
      requestAnimationFrame(() => {
        const container = scrollRef.current;
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth"
          });
        }
      });
    } else {
      return;
    }
  };

  const onSubmit = (data: GridSettingsValues) => {
    const ordering = data.columns.map((i) => i.id);
    const visibility = Object.fromEntries(
      availableColumns.map((col) => [col.id, data.columns.some((c) => c.id === col.id)])
    );
    const pinning = { left: [data.columns[0].id] };

    if (onSaveColumns) {
      onSaveColumns({ ordering, visibility, pinning }, data.columns);
    }
  };

  // DnD
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.fieldId === active.id);
    const newIndex = fields.findIndex((f) => f.fieldId === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl border-0 p-0 rounded-lg">
        {/* Modal container */}
        <div className="flex flex-col min-h-[550px] rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0">
            <div
              className={cn(
                "flex items-center justify-between bg-[#41875C] text-white px-4 py-2",
                headerClassname
              )}
            >
              <DialogTitle className="text-xl">Grid Settings</DialogTitle>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="flex items-center justify-center rounded-md hover:bg-white/10 transition cursor-pointer"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>

            <div
              className={cn(
                "bg-[#818181] text-white px-4 py-2.5 flex items-center",
                descriptionClassname
              )}
            >
              <DialogDescription className="text-white text-sm">
                Add or remove columns. To change the column order, drag and drop a field.
              </DialogDescription>
            </div>
          </div>

          {/* Scrollable */}
          <div className="flex flex-col flex-1 px-12 my-10 overflow-hidden">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1">
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto overflow-x-hidden max-h-[35vh] [scrollbar-gutter:stable_both-edges]"
                >
                  <div className="space-y-3 px-6">
                    <div className="[&_button:not([disabled])]:cursor-pointer">
                      <SortableRow
                        key={fields[0]?.fieldId}
                        value={fields[0]?.fieldId}
                        control={control}
                        name={`columns.0.id`}
                        isDragging={isDragging}
                        availableColumns={availableColumns}
                      />
                    </div>

                    <div className="space-y-3 [&_button:not([disabled])]:cursor-pointer">
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToParentElement, restrictToVerticalAxis]}
                        onDragStart={() => setIsDragging(true)}
                        onDragEnd={(event) => {
                          setIsDragging(false);
                          handleDragEnd(event);
                        }}
                      >
                        <SortableContext
                          items={fields?.slice(1).map((c) => c?.fieldId)}
                          strategy={verticalListSortingStrategy}
                        >
                          {fields?.slice(1).map((col, index) => (
                            <SortableRow
                              key={col?.fieldId}
                              value={col?.fieldId}
                              control={control}
                              name={`columns.${index + 1}.id`}
                              isDragging={isDragging}
                              availableColumns={availableColumns}
                              onRemove={() => remove(index + 1)}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-20 flex justify-center bg-white pt-3">
                  <Button
                    type="button"
                    className={cn(
                      "bg-[#49865d] hover:bg-[#326446] text-white w-[350px] rounded-t-lg transition-colors",
                      addButtonClassname
                    )}
                    onClick={addColumn}
                    disabled={fields.length >= limit}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add column
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Footer */}
          <DialogFooter className="bottom-0 border-t bg-white">
            <div className="flex gap-2 px-4 justify-end w-full">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="w-18 text-[#8B8B8B] border-[#B9B9B9]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={cn("w-18 bg-[#379A2A] hover:bg-[#2f8524] text-white", saveButtonClassname)}
                onClick={form.handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GridSettingsModal;
